import clsx from 'clsx'
import React from 'react'

import SvgBag2 from '@Assets/SVG/Bag2'
import SvgHear2 from '@Assets/SVG/Hear2'
import SvgSearch2 from '@Assets/SVG/Search2'
import SvgUser2 from '@Assets/SVG/User2'
import { Abundance } from '@Components/Badges'
import { CountryDetectionModal } from '@Components/CountryDetectionModal'
import { LanguageToggle } from '@Components/Header/LanguageToggle'
import { ImageV4 } from '@Components/Image'
import { Link } from '@Components/Link'
import { NativeModal, NativeModalHeader, useNativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import { SideCartContext } from '@Domains/Cart/Context/SideCartContextProvider'
import { GetNumberOfLineItemsInCart } from '@Domains/Cart/Feature/CartV2/Components/CartOverview/CartOverview'
import { useClub } from '@Domains/Club/Hook'
import { Menu } from '@Domains/Navigation/Features/Menu/Menu'
import { TopMenu } from '@Domains/Navigation/Features/Menu/TopMenu'
import { Search } from '@Domains/Navigation/Features/Search/Search'
import { useWishListCount } from '@Domains/WishList/Hooks'
import { enableBodyScroll, preventBodyScroll } from '@Globals/BodyHelpers'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'

import styles from './MainHeader.module.scss'

const CartButton = () => {
  const { t } = useTranslation()
  const { cartSummary, openCart } = React.useContext(SideCartContext)

  const numberOfProducts: number = React.useMemo(
    () => GetNumberOfLineItemsInCart(cartSummary?.lineItems || []),
    [cartSummary?.lineItems],
  )

  return (
    <div className={styles.cart}>
      <button
        aria-label={t('header:CART_SHORT_LABEL')}
        className={styles.icon}
        onClick={() => openCart()}
        type="button"
      >
        <Abundance className={clsx(styles.smallAbundance)} count={numberOfProducts}>
          <SvgBag2 />
        </Abundance>
      </button>
    </div>
  )
}

const SearchButton = () => {
  const { t } = useTranslation()
  const searchModalActionRef = useNativeModalActions()

  return (
    <div className={styles.search}>
      <button
        aria-label={t('header:WB_SEARCH_LABEL')}
        className={styles.mobileSearch}
        onClick={() => {
          searchModalActionRef.current?.open()
          preventBodyScroll()
        }}
        type="button"
      >
        <span>{t('header:WB_SEARCH_LABEL')}</span>
        <SvgSearch2 />
      </button>

      <NativeModal
        actionRef={searchModalActionRef}
        alignment="right"
        backdrop
        className={styles.searchDialog}
        closeOnBackDrop
        closeOnEscape
        onClose={() => enableBodyScroll()}
      >
        <NativeModalHeader className={styles.searchDialogClose} float />
        <Search />
      </NativeModal>
    </div>
  )
}

const WishListButton = () => {
  const { t } = useTranslation()
  const { storeURL } = React.useContext(MetadataContext)
  const itemsInWishlist = useWishListCount()

  return (
    <div className={styles.wish}>
      <Link
        analytics={{
          dest_page_type: 'wish-list',
          dest_page_type_id: '',
          source_id: 'header',
        }}
        aria-label={t('header:WISH_LIST_LABEL')}
        className="icon"
        href={storeURL('account/wishlist')}
      >
        <Abundance className={clsx(styles.smallAbundance)} count={itemsInWishlist}>
          <SvgHear2 />
        </Abundance>
      </Link>
    </div>
  )
}

const FrontpageLink = () => {
  const { storeURL } = React.useContext(MetadataContext)

  return (
    <div className={styles.logo}>
      <Link
        analytics={{
          dest_page_type: 'frontpages',
          dest_page_type_id: '',
          source_id: 'header',
        }}
        className="logo"
        href={storeURL('')}
      >
        <ImageV4
          alt="Trendhim"
          src="https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/blt59296545a01439bf/trendhim-logo-with-text-v2.svg"
          width={140}
        />
      </Link>
    </div>
  )
}

const AccountOverviewLink = () => {
  const { storeURL } = React.useContext(MetadataContext)
  const { isLoggedIn } = useClub()

  return (
    <Link
      analytics={{
        dest_page_type: 'account-overview',
        dest_page_type_id: '',
        source_id: 'header',
      }}
      className={clsx(styles.profile, isLoggedIn && styles.authenticated)}
      href={storeURL('account/overview')}
    >
      <SvgUser2 />
    </Link>
  )
}

export const MainHeader = React.memo(() => (
  <div className={styles.mainHeaderWrapper}>
    <div className={styles.mainHeader}>
      <CountryDetectionModal />
      <TopMenu className={styles.topmenu} />
      <FrontpageLink />
      <div className={styles.language}>
        <LanguageToggle showTitle={false} />
      </div>
      <SearchButton />
      <AccountOverviewLink />
      <WishListButton />
      <CartButton />
      <div className={styles.line} />
      <div className={styles.menu}>
        <Menu />
      </div>
    </div>
  </div>
))

MainHeader.displayName = 'MainHeader'
